import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Message } from "../../features/messages/models/Message";
import { PaymentReminderCard } from "../../features/messages/models/PaymentReminderCard";
import {
  formatCardAmount,
  formatCardDate,
  formatCardPeriod,
} from "../../features/messages/utils/paymentCardText";

interface PaymentCardProps {
  message: Message;
  card: PaymentReminderCard;
  /** Staff see "Open payment" into Billing; the member sees "Open payments" into the portal balance. */
  face: "staff" | "portal";
  /** Where the one link leads. */
  openTo: string;
}

/**
 * A payment reminder in the member's Direct thread: the author and time like any message, then a
 * hairline block with a thin accent rule — title, the amount in bold, the due date, one text link.
 * Overdue is said in words; no fill, no icon, no button.
 */
const PaymentCard = ({ message, card, face, openTo }: PaymentCardProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const time = new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
    message.createdAt,
  );
  const author = message.isMine ? t("messages.thread.you") : (message.authorName ?? "");
  const period = card.periodLabel ? formatCardPeriod(card.periodLabel, language) : null;

  return (
    <li className="py-3" data-testid="payment-card">
      <p className="text-xs text-grey-40 dark:text-grey-80">
        <span className="font-semibold text-grey-20 dark:text-white">{author}</span>
        <span aria-hidden="true"> · </span>
        <time dateTime={message.createdAt.toISOString()}>{time}</time>
      </p>
      <div className="mt-2 border border-l-2 border-grey-90 border-l-primary-40 px-4 py-3 dark:border-primary-20 dark:border-l-primary-90">
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {[t("messages.card.payment.title"), period].filter(Boolean).join(" · ")}
        </p>
        <p className="mt-1 text-lg font-semibold text-grey-20 dark:text-white" data-testid="payment-card-amount">
          {formatCardAmount(card, language)}
        </p>
        {card.dueDate && (
          <p className="text-sm text-grey-20 dark:text-white" data-testid="payment-card-due">
            {t("messages.card.payment.due", { date: formatCardDate(card.dueDate, language) })}
          </p>
        )}
        <Link to={openTo} className="mt-2 inline-block text-sm text-primary-40 hover:underline dark:text-primary-90">
          {face === "staff" ? t("messages.card.payment.openStaff") : t("messages.card.payment.open")}
        </Link>
      </div>
    </li>
  );
};

export default PaymentCard;
